import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { validateImage } from 'image-validator';
import { QuillEditorComponent } from 'ngx-quill';
import { PublicationQuillEditorHelper } from '../../../../clubs/helpers/publication-quill-editor.helper';
import { ClubPublicationService } from '../../../../clubs/services/publication.service';
import { EmojiHelper } from '../../../../helpers/emoji.helper';
import { QuillEditorHelper } from '../../../../helpers/quill-editor.helper';
import { SpinnerHelper } from '../../../../helpers/spinner.helper';
import { ToastrHelper } from '../../../../helpers/toastr.helper';
import { Member } from '../../../../models/clubs/member.model';
import { MentionMember } from '../../../../models/clubs/mention-member.model';
import { MemberApiService } from '../../../../services/clubs/member.api.service';
import { PublicationService } from '../../../../services/clubs/publication.service';
import { DocumentsService } from '../../../../services/dms/documents.service';
import { AccountsService } from '../../../../services/identity/accounts.service';
import { WudoModalComponent } from '../../../../shared/wudo-components/wudo-modal/wudo-modal.component';
import { ClubsStore } from './../../../../clubs/clubs.store';
import { messages } from './../../../../constants/messages';
import { Publication } from './../../../../models/clubs/publication.model';
import { User } from './../../../../models/identity/user.model';
import { CropperParams } from './../../../../models/shared/cropper-params.model';
import { ImageService } from './../../../../services/shared/image.service';

@Component({
    selector: 'update-comment-modal-button',
    styleUrls: ['./update-comment-modal-button.component.scss'],
    templateUrl: './update-comment-modal-button.component.html',
})
export class UpdateCommentButtonModalComponent implements OnInit, AfterViewInit {
    private documentHasChanged = false;
    private isInitializingCropper = false;

    public readonly PUBLICATION_CONTENT_MAX_LENGTH = 2000;
    @ViewChild('emojiButton') emojiButton: ElementRef;
    @ViewChild('emojiPicker') emojiPicker: ElementRef;
    public LocalDocument: File;
    public action: string;
    @Output()
    public afterCommentUpdate = new EventEmitter<Publication>();
    public commentText: string;
    public cropperParams: CropperParams = new CropperParams();
    public currentUser: User;
    @Input() public disabled: boolean;
    public emojiTranslations: unknown;
    @Input() public existingPublication: Publication;
    public form = this.fb.group({
        content: ['', [Validators.required, this.plainTextMaxLength(this.PUBLICATION_CONTENT_MAX_LENGTH)]],
        clubId: [null, [Validators.required]],
        userId: [null, []],
        postId: [null, []],
        image: [null, []],
        picture: [null, []],
        attachedDocumentId: [null, []],
        attachedUrl: [null, []],
        pictureHasChanged: [null, []],
        gif: [null, []],
        mentionMembers: [null, []],
    });
    public hasPictureInBegining = false;
    @Input() public isEditMode = true;
    public isEmojiPickerVisible = false;
    public isMobile = false;
    public isModalDisplayed = false;
    public localImageSrc = '';
    public members: Array<Member>;
    public messages = messages;

    @ViewChild('modal') public modal: WudoModalComponent;
    @ViewChild('newPublicationQuillEditor') public newPublicationQuillEditor: QuillEditorComponent;
    public pictureHasBeenDeleted = false;
    public pictureHasChanged = false;
    public pos: number;
    public submitted = false;
    public title: string;
    public urlLink: string;

    constructor(
        private readonly accountService: AccountsService,
        private readonly breakpointObserver: BreakpointObserver,
        public readonly clubsStore: ClubsStore,
        private readonly documentService: DocumentsService,

        private readonly eRef: ElementRef,
        private readonly emojiHelper: EmojiHelper,
        private readonly fb: FormBuilder,
        public readonly imageService: ImageService,
        private readonly memberService: MemberApiService,
        private readonly publicationApiService: PublicationService,
        private readonly publicationService: ClubPublicationService,
        readonly publicationQuillEditorHelper: PublicationQuillEditorHelper,
        public readonly quillEditorHelper: QuillEditorHelper,
        private readonly spinnerHelper: SpinnerHelper,
        private readonly toastrHelper: ToastrHelper,
        private translate: TranslateService,
    ) {
        this.breakpointObserver.observe([Breakpoints.HandsetPortrait]).subscribe((result) => {
            this.isMobile = result.matches;
        });
    }

    public get contentLength(): number {
        if (!this.newPublicationQuillEditor) {
            return 0;
        }

        return this.quillEditorHelper.getPlainTextLength(this.form.controls.content?.value);
    }

    public get currentClubTitle(): string {
        return this.clubsStore.displayedClub.title ?? this.translate.instant('clubs.default.defaultRoomName');
    }

    public get currentRoomTitle(): string {
        return this.clubsStore.displayedRoom.title === this.clubsStore.displayedClub.title
            ? this.translate.instant('clubs.default.defaultRoomName')
            : this.clubsStore.displayedRoom.title;
    }

    public get formDoesNotContainContent(): boolean {
        return this.isContentEmpty() && this.isPictureEmpty();
    }

    public addEmojiToContent(data: any) {
        this.pos = this.quillEditorHelper.getCaretPositionQuill(this.newPublicationQuillEditor);

        const emoji = data?.emoji?.native;

        if (!emoji) {
            console.error("can't find emoji");
            return;
        }

        this.newPublicationQuillEditor.quillEditor.insertText(this.pos, emoji);

        const updatedContent = this.newPublicationQuillEditor.quillEditor.root.innerHTML;

        this.form.controls.content.setValue(updatedContent);

        this.newPublicationQuillEditor.quillEditor.setSelection(this.pos + emoji.length);
    }

    public closeModal() {
        this.isEmojiPickerVisible = false;
        this.isModalDisplayed = false;
        this.members = [];
        this.resetForm();
    }
    @HostListener('document:click', ['$event'])
    public clickout(event: Event) {
        if (this.isEmojiPickerVisible) {
            const emojiPickerElement = document.querySelector('.emoji-picker-container');
            const emojiButton = document.querySelector('.picker-button');
            const isClickedInsidePicker = emojiPickerElement && emojiPickerElement.contains(event.target as Node);
            const isClickedOnButton = emojiButton && emojiButton.contains(event.target as Node);

            if (!isClickedInsidePicker && !isClickedOnButton) {
                this.isEmojiPickerVisible = false;
            }
        }
    }
    public handleEmojiPickerButtonClick() {
        this.pos = this.quillEditorHelper.getCaretPositionQuill(this.newPublicationQuillEditor);
        this.isEmojiPickerVisible = !this.isEmojiPickerVisible;
    }

    public async handleOnCancel() {
        this.closeModal();
        if (this.isEditMode) await this.initValuesIfEditMode();
    }

    public async handleOnSubmit() {
        this.saveMentionMembers();

        if (this.formDoesNotContainContent) {
            this.toastrHelper.showError('Un commentaire ne peut pas être vide.');
            return;
        }

        this.spinnerHelper.showSpinner();

        try {
            this.submitted = true;
            this.initCommonValues();

            if (this.form.controls.picture.value != null) {
                const compressedImage = await this.imageService.compressImage(this.form.controls.picture.value);
                this.form.controls.picture.setValue(compressedImage);
            }

            await this.editComment();
        } catch (error) {
            console.error(error);
        } finally {
            this.submitted = false;
        }

        this.spinnerHelper.hideSpinner();
    }

    public handlePaste(event: ClipboardEvent) {
        const clipboardData = event.clipboardData;

        if (clipboardData.items && clipboardData.items.length) {
            const item = clipboardData.items[0];
            if (this.canUploadImage()) {
                if (this.isImage(item)) {
                    const file = item.getAsFile();
                    this.setImageInForm(file);
                } else if (item.type === 'text/html') {
                    event.preventDefault();
                }
            }
        }
    }

    public handleRemoveImage() {
        if (this.isEditMode) {
            this.pictureHasChanged = true;
            this.cropperParams.urlImage = '';
            this.pictureHasBeenDeleted = true;
        }

        this.localImageSrc = '';
        this.form.controls.image.setValue(null);
        this.form.controls.picture.setValue(null);
    }

    public imageHasBeenCropped(event: any) {
        this.imageService.imageCropped(event);
        if (!this.isInitializingCropper) {
            this.pictureHasChanged = true;
        }
        this.isInitializingCropper = false; // Reset flag after first event
    }

    public initCommonValues() {
        if (this.form.controls.content.value == null) this.form.controls.content.setValue('');
        this.form.controls.userId.setValue(this.currentUser.userId);
        this.form.controls.clubId.setValue(this.clubsStore.displayedRoomId);
    }

    public async initValuesIfEditMode() {
        if (this.newPublicationQuillEditor) {
            this.newPublicationQuillEditor.quillEditor.root.innerHTML =
                this.existingPublication?.content?.length > 0 ? this.existingPublication.content : '';
        }

        this.form.controls.postId.setValue(this.existingPublication.id);
        this.form.controls.pictureHasChanged.setValue(false);
        this.pictureHasChanged = false;
        this.hasPictureInBegining = this.existingPublication.pictureAccessLink != null;

        this.localImageSrc = null;
        this.cropperParams.urlImage = this.existingPublication.pictureAccessLink;
        this.pictureHasBeenDeleted = false;

        // Load existing image as base64 for proper display in cropper
        if (this.existingPublication.pictureAccessLink) {
            this.isInitializingCropper = true; // Set flag to prevent setting pictureHasChanged on initial load
            try {
                const file = await this.imageService.fetchImageViaProxy(this.existingPublication.pictureAccessLink);
                if (file) {
                    const reader = new FileReader();
                    reader.onload = (event: any) => {
                        this.localImageSrc = event.target.result;
                    };
                    reader.readAsDataURL(file);
                }
            } catch (error) {
                this.isInitializingCropper = false; // Reset flag on error
            }
        }

        this.form.controls.mentionMembers.setValue(this.existingPublication.mentionMembers);
    }

    public async ngAfterViewInit() {
        if (this.isEditMode) await this.initValuesIfEditMode();
    }

    public ngOnInit() {
        this.currentUser = this.accountService.currentUser;
        this.resetForm();

        const titleKey = 'clubs.page.tabs.discussion.post.comment.editCommentTitle';
        this.title = this.translate.instant(titleKey);

        const actionKey = 'components.button.modify';
        this.action = this.translate.instant(actionKey);

        this.cropperParams.isRatioFix = false;
        this.cropperParams.resizeToHeight = 1000;
        this.cropperParams.resizeToWidth = 1000;

        this.emojiTranslations = this.emojiHelper.getTranslations();
    }

    public onKeydown(event: KeyboardEvent) {
        if (event.key === 'Tab') {
            event.preventDefault();

            const editor = this.newPublicationQuillEditor.quillEditor;

            setTimeout(() => {
                const selection = editor.getSelection();
                if (selection) {
                    editor.insertText(selection.index, '    ');
                    editor.setSelection(selection.index, 4);
                }
            }, 0);
        }
    }

    public onMentionListClosed() {
        this.publicationService.setMentionOpened(false);
    }

    public onMentionListOpen() {
        this.publicationService.setMentionOpened(true);
    }

    public onMentionSelected($event: MentionMember) {
        const value = this.form.controls.mentionMembers.value || [];
        value.push($event);
        this.form.controls.mentionMembers.setValue(value);
    }

    public openModal() {
        this.members = this.clubsStore.flatMembers;

        if (this.existingPublication.userId == this.currentUser.userId) {
            this.form.controls.mentionMembers.setValue(this.existingPublication.mentionMembers);
            this.isModalDisplayed = true;
        } else {
            this.toastrHelper.showError("Vous n'avez pas la permission de modifier ce commentaire.");
        }
    }

    public plainTextMaxLength(maxLength: number) {
        return (control: AbstractControl): ValidationErrors | null => {
            return this.contentLength > maxLength ? { maxlength: { requiredLength: maxLength, actualLength: this.contentLength } } : null;
        };
    }

    public resetForm() {
        this.form.reset();
        this.localImageSrc = '';
        this.urlLink = '';
        this.initCommonValues();
        this.form.controls.mentionMembers.setValue(null);
        this.LocalDocument = null;
    }

    public updateImage(event: any) {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            this.setImageInForm(file);
        }
    }

    private async editComment() {
        this.form.controls.pictureHasChanged.setValue(this.pictureHasChanged);

        const plainContent = this.quillEditorHelper.getPlainTextWithLineBreaks(this.form.controls.content.value);
        this.form.controls.content.setValue(plainContent);

        const commentUpdated: Publication = await this.publicationApiService.editPublication(this.form.value).toPromise();

        this.toastrHelper.showSuccess(this.translate.instant('clubs.page.tabs.discussion.post.commentModified'));

        this.afterCommentUpdate.emit(commentUpdated);
        this.closeModal();
    }

    private isContentEmpty(): boolean {
        return this.form?.controls.content.value == null || this.form?.controls.content.value.length <= 0;
    }

    private isImage(item: DataTransferItem) {
        return item.type.indexOf('image') !== -1;
    }

    private isLocalImageSrcEmpty(): boolean {
        return this.localImageSrc == null || this.localImageSrc == '';
    }

    private isPictureEmpty(): boolean {
        if (this.isEditMode) {
            return this.isPictureEmptyForEditMode();
        } else {
            return this.isPictureEmptyForCreateMode();
        }
    }

    private isPictureEmptyForCreateMode(): boolean {
        return this.form?.controls.picture.value == null && this.isLocalImageSrcEmpty();
    }

    private isPictureEmptyForEditMode(): boolean {
        if (this.hasPictureInBegining && this.pictureHasBeenDeleted) {
            return this.isLocalImageSrcEmpty();
        } else if (!this.hasPictureInBegining) {
            return this.isLocalImageSrcEmpty();
        }
        return false;
    }

    private saveMentionMembers() {
        let value = this.form.controls.mentionMembers.value;
        if (value) {
            const content = this.form.controls.content.value;
            value = value.filter((mention: MentionMember) => content.search(mention.tagMember) > -1);
            this.form.controls.mentionMembers.setValue(value);
        }
    }

    private canUploadImage() {
        return true;
    }

    private async setImageInForm(file: File) {
        if (!file) {
            return;
        }
        if (await validateImage(file)) {
            this.setImage(file);
        } else {
            this.toastrHelper.showWarning(this.translate.instant('toastMessages.pictureExtensionError'));
        }
    }

    private setImage(file: File) {
        const reader = new FileReader();
        reader.onload = (event: any) => {
            this.localImageSrc = event.target.result;
            if (this.isEditMode) this.pictureHasChanged = true;
            this.form.controls.picture.setValue(<File>file);
        };
        reader.readAsDataURL(file);
    }
}
